import dayjs from 'dayjs'
import { array, boolean, mixed, number, object, string } from 'yup'

import { getTimezone, roundToNext30Minutes } from '../../helpers'
import { EventVisibilityType } from '../../types/events'
import { DEFAULT_RECURRING_AFTER_OCCURRENCES, EventLocation } from '../../constants'
import type { EventFormDataType } from '../../types/events'

// Constants
export const eventTitleMaxLength = 100
export const eventTitleMinLength = 1
export const eventDescriptionMaxLength = 5000
export const eventLocationMaxLength = 500
export const eventLocationUrlMaxLength = 2000
export const minEventPrice = 0
export const maxEventPrice = 999999

// Error messages
export enum EventValidationErrorMessages {
  TITLE_REQUIRED = 'Event title is required',
  DESCRIPTION_REQUIRED = 'Description is required',
  TITLE_TOO_SHORT = 'Title cannot be empty',
  TITLE_TOO_LONG = 'Title cannot exceed 100 characters',
  COLOR_REQUIRED = 'Event color is required',
  COLOR_INVALID = 'Invalid color format',
  START_TIME_REQUIRED = 'Start time is required',
  START_TIME_MUST_BE_FUTURE = 'Start time must be in the future',
  END_TIME_MUST_BE_FUTURE = 'End time must be in the future',
  END_TIME_REQUIRED = 'End time is required',
  END_TIME_BEFORE_START = 'End time must be after start time',
  EVENT_DURATION_TOO_LONG = 'The maximum duration of an event is 24 hours',
  LOCATION_TOO_LONG = 'Location cannot exceed 500 characters',
  LOCATION_URL_INVALID = 'Invalid URL format',
  LOCATION_URL_TOO_LONG = 'Location URL cannot exceed 2000 characters',
  DESCRIPTION_TOO_LONG = 'Description cannot exceed 5000 characters',
  PRICE_INVALID = 'Price must be a valid number',
  PRICE_NEGATIVE = 'Price cannot be negative',
  PRICE_TOO_HIGH = 'Price cannot exceed 999,999',
  CURRENCY_REQUIRED = 'Currency is required',
  REPEAT_OCCURRENCE_INVALID = 'Repeat occurrence must be at least 1',
  REPEAT_INTERVAL_REQUIRED = 'Repeat interval is required',
  AFTER_OCCURRENCES_INVALID = 'Number of occurrences must be at least 1',
  TIMEZONE_REQUIRED = 'Timezone is required',
  LOCATION_REQUIRED = 'Location is required',
  START_DATE_REQUIRED = 'Start date is required',
  END_DATE_REQUIRED = 'End date is required',
  LOCATION_URL_REQUIRED = 'Location URL is required',
  LOCATION_LINK_OR_ADDRESS_REQUIRED = 'Link or address is required',
  LOCATION_URL_NOT_GOOGLE_MEET = 'Please enter a valid Google Meet link',
  LOCATION_URL_NOT_ZOOM = 'Please enter a valid Zoom link',
}

// Validation schemas
export const eventTitleValidation = (t?: any) =>
  string()
    .required(EventValidationErrorMessages.TITLE_REQUIRED)
    .min(eventTitleMinLength, EventValidationErrorMessages.TITLE_REQUIRED)
    .max(eventTitleMaxLength, EventValidationErrorMessages.TITLE_TOO_LONG)
    .trim()

export const eventColorValidation = () =>
  string()
    .required(EventValidationErrorMessages.COLOR_REQUIRED)
    .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, EventValidationErrorMessages.COLOR_INVALID)

export const eventDescriptionValidation = () =>
  string()
    .required(EventValidationErrorMessages.DESCRIPTION_REQUIRED)
    .max(eventDescriptionMaxLength, EventValidationErrorMessages.DESCRIPTION_TOO_LONG)

export const eventLocationValidation = () =>
  string()
    .required(EventValidationErrorMessages.LOCATION_REQUIRED)
    .max(eventLocationMaxLength, EventValidationErrorMessages.LOCATION_TOO_LONG)
    .optional()

export const eventPriceValidation = () =>
  number()
    .min(minEventPrice, EventValidationErrorMessages.PRICE_NEGATIVE)
    .max(maxEventPrice, EventValidationErrorMessages.PRICE_TOO_HIGH)
    .test('is-valid-price', EventValidationErrorMessages.PRICE_INVALID, value => {
      if (value === undefined || value === null) return true
      return !isNaN(value) && isFinite(value)
    })

// Complete event form validation schema
export const createEventSchema = (t?: any) =>
  object().shape({
    skipTimezoneValidation: boolean(), // skip timezone validation for editing events
    selectedStartTime: mixed().nullable().optional(),
    selectedEndTime: mixed().nullable().optional(),
    title: eventTitleValidation(t),
    description: eventDescriptionValidation(),
    eventCoverUrl: string().optional(),
    eventColor: eventColorValidation(),
    timezone: string().optional(),
    hideAttendees: boolean(),
    remindOneDayBefore: boolean(),
    startTime: mixed()
      .required(EventValidationErrorMessages.START_DATE_REQUIRED)
      .test('is-future', EventValidationErrorMessages.START_TIME_MUST_BE_FUTURE, function (value) {
        if (!value) return true
        const { selectedStartTime } = this.parent

        // Edit mode: allow if equal to original start time OR in the future
        if (selectedStartTime) {
          const newTime = dayjs(value as string).valueOf()
          const originalTime = dayjs(selectedStartTime as string).valueOf()
          const now = dayjs().valueOf()

          return newTime === originalTime || newTime > now
        }
        // Create mode: time must be in the future
        return dayjs(value as string).valueOf() > dayjs().valueOf()
      }),

    endTime: mixed()
      .required(EventValidationErrorMessages.END_DATE_REQUIRED)
      .test('is-after-start', EventValidationErrorMessages.END_TIME_BEFORE_START, function (value) {
        const { startTime } = this.parent
        if (!startTime || !value) {
          return true
        }
        return dayjs(value as string).valueOf() > dayjs(startTime as string).valueOf()
      })
      .test('is-future', EventValidationErrorMessages.END_TIME_MUST_BE_FUTURE, function (value) {
        if (!value) return true
        const { selectedEndTime, startTime } = this.parent

        // Edit mode: allow if equal to original end time OR in the future
        if (selectedEndTime) {
          const newTime = dayjs(value as string).valueOf()
          const originalTime = dayjs(selectedEndTime as string).valueOf()
          const now = dayjs().valueOf()
          const startTimeValue = dayjs(startTime as string).valueOf()

          // Must be either equal to original OR (in future AND greater than start time)
          return newTime === originalTime || (newTime > now && newTime > startTimeValue)
        }

        // Create mode: time must be in the future and greater than start time
        const newTime = dayjs(value as string).valueOf()
        const now = dayjs().valueOf()
        const startTimeValue = startTime ? dayjs(startTime as string).valueOf() : 0

        return newTime > now && newTime > startTimeValue
      })
      .test('max-duration', EventValidationErrorMessages.EVENT_DURATION_TOO_LONG, function (value) {
        const { startTime } = this.parent
        if (!startTime || !value) {
          return true
        }
        const durationInHours = dayjs(value as string).diff(dayjs(startTime as string), 'hour', true)
        return durationInHours <= 24
      }),
    isFree: boolean(),
    isCancelled: boolean(),
    isRecurring: boolean(),
    timeZone: string().optional(),
    recurringMeta: object().shape({
      eventStart: mixed().nullable().optional(),
      eventEnd: mixed().nullable().optional(),
      rrule: string().optional(),
      excludeDates: array().of(mixed()).optional(),
      repeatOccurence: number().optional(),
      repeatInterval: string().optional(),
      repeatsOn: array().of(string()).optional(),
      recurringEndDate: mixed().nullable().optional().default(null),
      monthlyRepeatType: string().optional(),
      afterOccurrences: number().optional(),
      isNeverEnds: boolean().optional(),
    }),
    notification: object().shape({
      notifyAt: string().optional(),
      cloudTaskId: string().optional(),
    }),
    status: string().oneOf(['active', 'cancelled'], 'Invalid status').required('Status is required'),
    eventLocation: object().shape({
      location: eventLocationValidation(),
      locationUrl: string()
        .max(eventLocationUrlMaxLength, EventValidationErrorMessages.LOCATION_URL_TOO_LONG)
        .when('location', ([location], schema) => {
          if (location === EventLocation.MEET) {
            return schema
              .required(EventValidationErrorMessages.LOCATION_URL_REQUIRED)
              .url(EventValidationErrorMessages.LOCATION_URL_INVALID)
              .test(
                'is-google-meet',
                EventValidationErrorMessages.LOCATION_URL_NOT_GOOGLE_MEET,
                (value: string | undefined) => !value || /^https?:\/\/meet\.google\.com\//i.test(value)
              )
          }
          if (location === EventLocation.ZOOM) {
            return schema
              .required(EventValidationErrorMessages.LOCATION_URL_REQUIRED)
              .url(EventValidationErrorMessages.LOCATION_URL_INVALID)
              .test(
                'is-zoom',
                EventValidationErrorMessages.LOCATION_URL_NOT_ZOOM,
                (value: string | undefined) => !value || /^https?:\/\/([a-z0-9-]+\.)?zoom\.us\//i.test(value)
              )
          }
          if (location === EventLocation.LIVE_ROOM) {
            return schema.optional()
          }
          if (location === EventLocation.OTHER) {
            return schema
              .transform((value: unknown) => (typeof value === 'string' ? value.trim() : value))
              .required(EventValidationErrorMessages.LOCATION_LINK_OR_ADDRESS_REQUIRED)
              .min(1, EventValidationErrorMessages.LOCATION_LINK_OR_ADDRESS_REQUIRED)
          }
          return schema.optional()
        }),
      hideEventLocationFromNonAttendees: boolean(),
    }),

    visibility: object().shape({
      type: string().oneOf(Object.values(EventVisibilityType), 'Invalid visibility type').required('Visibility type is required'),
      level: string().when('type', {
        is: EventVisibilityType.LEVEL,
        then: schema => schema.required('Level is required'),
        otherwise: schema => schema.optional(),
      }),
      course: string().when('type', {
        is: EventVisibilityType.COURSE,
        then: schema => schema.required('Course is required'),
        otherwise: schema => schema.optional(),
      }),
      privateChannel: string().when('type', {
        is: EventVisibilityType.CHANNEL,
        then: schema => schema.required('Channel is required'),
        otherwise: schema => schema.optional(),
      }),
    }),
    eventPricing: object().shape({
      price: number()
        .test('is-valid-price', EventValidationErrorMessages.PRICE_INVALID, value => {
          if (value === undefined || value === null) return true
          return !isNaN(value) && isFinite(value)
        })
        .test('price-required-for-paid', 'Price is required for paid events', function (value) {
          const isFree = this.from?.[1]?.value?.isFree
          if (isFree === false) {
            return value !== undefined && value !== null
          }
          return true
        })
        .test('price-min-for-paid', 'Price must be greater than 0', function (value) {
          const isFree = this.from?.[1]?.value?.isFree
          if (isFree === false && value !== undefined && value !== null) {
            return value > 0
          }
          return true
        })
        .min(0, EventValidationErrorMessages.PRICE_NEGATIVE),
      currency: string().test('currency-required-for-paid', EventValidationErrorMessages.CURRENCY_REQUIRED, function (value) {
        const isFree = this.from?.[1]?.value?.isFree
        if (isFree === false) {
          return !!value
        }
        return true
      }),
      isTestMode: boolean(),
    }),
  })

export const initialFormData = (): EventFormDataType => ({
  title: '',
  description: '',
  eventCoverUrl: '',
  eventColor: '#ff5c00',
  timezone: getTimezone(),
  hideAttendees: false,
  remindOneDayBefore: true,
  startTime: roundToNext30Minutes(new Date()),
  endTime: roundToNext30Minutes(new Date()).add(1, 'hour'),
  isFree: true,
  isCancelled: false,
  isRecurring: false,
  recurringEndType: 'never',
  recurringMeta: {
    rrule: '',
    excludeDates: [],
    repeatOccurence: 1,
    repeatInterval: 'day',
    repeatsOn: [],
    monthlyRepeatType: 'monthly_day',
    afterOccurrences: DEFAULT_RECURRING_AFTER_OCCURRENCES,
  },
  status: 'active',
  eventLocation: {
    location: 'other',
    locationUrl: '',
    hideEventLocationFromNonAttendees: false,
  },

  visibility: {
    type: EventVisibilityType.ALL,
    level: '',
    course: '',
    privateChannel: '',
  },
  eventPricing: {
    price: 0,
    currency: 'USD',
    isTestMode: false,
  },
  notification: {
    notifyAt: '',
    cloudTaskId: '',
  },
})
